import {Params} from '@angular/router';
import {ChildRouteNamesFor, MxRouteConstants, MxRouterTabIndexFor, MxRouterTabsFor, MxRoutes, PathFor} from '@aviary/store/router/router.types';
import {extractChildRouteNamesFor, extractPathFor, extractRouterTabIndexFor, extractRouterTabsFor} from '@aviary/store/router/router.extractors';
import {isUndefined} from '@aviary/store/common/typing.helpers';

export function buildMxRouteConstantsFrom<RouteNameType extends string = string>(namedRoutes: MxRoutes<RouteNameType>): MxRouteConstants<RouteNameType> {
  const PATH_FOR: PathFor<RouteNameType> = extractPathFor(namedRoutes);
  const CHILD_ROUTE_NAMES_FOR: ChildRouteNamesFor<RouteNameType> = extractChildRouteNamesFor(namedRoutes);
  const ROUTER_TABS_FOR: MxRouterTabsFor<RouteNameType> = extractRouterTabsFor(namedRoutes);
  const ROUTER_TAB_INDEX_FOR: MxRouterTabIndexFor<RouteNameType> = extractRouterTabIndexFor(ROUTER_TABS_FOR);

  return {PATH_FOR, CHILD_ROUTE_NAMES_FOR, ROUTER_TABS_FOR, ROUTER_TAB_INDEX_FOR};
}

export function buildValidatedRouterPathFrom(routeName: string, params: Params, pathFor: PathFor): string {
  const rawRouterPath: string | undefined = pathFor[routeName];
  if (isUndefined(rawRouterPath)) throw new Error(`Can't find '${routeName}' in PATH_FOR. Is this name defined in some route's data?`);

  validateParamsFor(rawRouterPath, params, routeName);

  return substituteParamsIn(rawRouterPath, params);
}

//
// Local functions
//
function isParameterPlaceholder(segment: string): boolean {
  return segment.startsWith(':');
}

function asParameterName(segment: string): string {
  return segment.replace(/^:/, '');
}

function substituteParamsIn(rawPath: string, params: Params): string {
  return rawPath
    .split('/')
    .map(segment => isParameterPlaceholder(segment) ? params[asParameterName(segment)] : segment)
    .join('/');
}

function validateParamsFor(rawRouterPath: string, params: Params, routeName: string): void {
  const routeParamNames: string [] =
    rawRouterPath
      .split('/')
      .filter(segment => isParameterPlaceholder(segment))
      .map(segment => asParameterName(segment));

  const specifiedParamNames: string[] = Object.keys(params);

  checkForMissingParams(routeParamNames, specifiedParamNames, routeName, rawRouterPath);
  checkForExtraParams(routeParamNames, specifiedParamNames, routeName, rawRouterPath);
}

function checkForMissingParams(routeParamNames: string[], specifiedParamNames: string[], routeName: string, rawRouterPath: string): void {
  const missingParamValues: string[] = routeParamNames.filter(routeParam => !specifiedParamNames.includes(routeParam));

  if (missingParamValues.length > 0) {
    throw new Error(
      `'${routeName}' maps to the path '${rawRouterPath}', but the [hkRouterLinkParams] input is missing the following parameters: `
      + missingParamValues.join(', ')
    );
  }
}

function checkForExtraParams(routeParamNames: string[], specifiedParamNames: string[], routeName: string, rawRouterPath: string): void {
  const extraParamValues: string[] = specifiedParamNames.filter(specifiedParam => !routeParamNames.includes(specifiedParam));

  if (extraParamValues.length > 0) {
    throw new Error(
      `'${routeName}' maps to the path '${rawRouterPath}', and the [hkRouterLinkParams] input supplies the following unneeded parameters: `
      + extraParamValues.join(', ')
    );
  }
}
